import { useState } from "react";
import { EmailLink } from "./EmailLink";
import * as Tabs from "@radix-ui/react-tabs";
import { Check } from "lucide-react";
import { cn } from "../lib/cn";
import { signUpUrl } from "../lib/links";
import { home } from "../content/home";

type Cycle = "monthly" | "yearly";

// Pricing CTAs send people to the app to sign up (plan-agnostic, so the copy
// survives future non-Chrome surfaces). Install-flavoured "Add to Chrome" CTAs
// stay in the hero, navbar, and final CTA where install intent is clearest.
const SALES_EMAIL = "hello@intakall.io";
const SALES_SUBJECT = "Intakall Enterprise";

const { label, headline, tiers } = home.pricing;

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";
const trigger = cn(
  "group rounded-full px-5 py-1.5 font-sans text-sm font-semibold transition-colors duration-200",
  "data-[state=active]:bg-char data-[state=active]:text-paper",
  "data-[state=inactive]:text-char/60 data-[state=inactive]:hover:text-char",
  focusRing,
);

function formatPrice(value: number) {
  return `$${value}`;
}

export function PricingSection() {
  const [cycle, setCycle] = useState<Cycle>("monthly");

  return (
    <section id="pricing" aria-label={label} className="scroll-mt-20 px-4 py-24 sm:px-6 lg:py-28">
      <div className="mx-auto max-w-6xl">
        <h2 className="font-serif text-[clamp(2.25rem,4.5vw,3.5rem)] font-medium leading-[1.02] tracking-[-0.015em] text-char">
          {headline.line1}
          <br />
          <em className="text-brand-500">{headline.line2}</em>
        </h2>

        <Tabs.Root value={cycle} onValueChange={(v) => setCycle(v as Cycle)} className="mt-10">
          <Tabs.List
            aria-label="Billing cycle"
            className="inline-flex rounded-full border border-char bg-paper-raised p-1"
          >
            <Tabs.Trigger value="monthly" className={trigger}>
              Monthly
            </Tabs.Trigger>
            <Tabs.Trigger value="yearly" className={trigger}>
              Yearly
              <span className="ml-2 font-mono text-[11px] text-brand-600 group-data-[state=active]:text-paper">
                -20%
              </span>
            </Tabs.Trigger>
          </Tabs.List>

          {(["monthly", "yearly"] as const).map((panel) => (
            <Tabs.Content key={panel} value={panel} forceMount asChild>
              <div
                data-testid={`pricing-panel-${panel}`}
                hidden={cycle !== panel}
                className={cn(
                  "mt-10 gap-6 lg:gap-8",
                  // display:grid only when active, so the `hidden` attribute isn't
                  // overridden by a display utility on the inactive panel.
                  cycle === panel && "grid grid-cols-1 lg:grid-cols-3",
                )}
              >
                <PricingGrid cycle={panel} />
              </div>
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </section>
  );
}

function PricingGrid({ cycle }: { cycle: Cycle }) {
  return (
    <>
      {tiers.map((tier) => {
        const amount = cycle === "yearly" ? tier.yearly : tier.monthly;
        const highlighted = "highlighted" in tier && tier.highlighted;
        const ctaClass = cn(
          "mt-7 inline-flex h-11 items-center justify-center rounded-[10px] border border-char font-sans text-[15px] font-semibold transition-[transform,box-shadow] duration-150 hover:-translate-x-px hover:translate-y-px",
          highlighted ? "bg-brand-600 text-white shadow-offset-sm" : "bg-paper text-char shadow-offset-sm",
          focusRing,
        );
        return (
          <article
            key={tier.name}
            className={cn(
              "relative flex h-full flex-col rounded-[22px] border border-char p-7 md:p-8",
              highlighted ? "bg-white shadow-[-5px_6px_0_0_#006fbd]" : "bg-paper-raised shadow-offset-sm",
            )}
          >
            {highlighted && (
              <span className="absolute -top-4 right-6 rotate-3 bg-[#e3f2fb] px-3 py-1 font-hand text-xl text-char shadow-[0_6px_12px_-8px_rgb(38_37_36/0.5)]">
                Most popular
              </span>
            )}
            <h3 className="font-serif text-3xl text-char">{tier.name}</h3>
            <p className="mt-1 font-sans text-sm leading-relaxed text-char/70 lg:min-h-[2.75rem]">{tier.tagline}</p>
            <div className="mt-6 flex items-baseline gap-2">
              {"pricePrefix" in tier && (
                <span className="font-sans text-sm font-medium text-char/70">{tier.pricePrefix}</span>
              )}
              <span className="font-serif text-6xl leading-none text-char">{formatPrice(amount)}</span>
              <span className="font-sans text-sm text-char/70">{tier.unit}</span>
            </div>
            {/* Always takes its line so prices and buttons align across cards. */}
            <p className="mt-2 h-4 font-sans text-xs text-char/60">
              {cycle === "yearly" && tier.monthly > 0 && `Billed annually at $${tier.yearly * 12}`}
            </p>
            {tier.name === "Enterprise" ? (
              <EmailLink email={SALES_EMAIL} subject={SALES_SUBJECT} wrapperClassName="flex flex-col" className={ctaClass}>
                {tier.cta}
              </EmailLink>
            ) : (
              <a href={signUpUrl("pricing")} className={ctaClass}>
                {tier.cta}
              </a>
            )}
            <ul className="mt-7 space-y-3 border-t border-char/15 pt-6">
              {tier.features.map((feat) => (
                <li key={feat} className="flex items-start gap-3 font-sans text-sm text-char/80">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </>
  );
}
